import { useCallback, useState } from 'react';
import { MAX_FOLDER_DEPTH } from '../../../../constants/folder';
import { getAPIErrorMessage } from '../../../../helpers/helpers';
import { FolderService } from '../../../../service/FolderService';
import {
  NotificationType,
  useNotificationStore,
} from '../../../../store/storeNotifications';
import { useSidebarTreeStore } from '../../../../store/storeSidebarTree';
import type { Folder } from '../../../../util/Types/Folder';
import { wouldExceedDepth } from '../dnd/folderDepth';

type Section = 'agents' | 'data';

interface UseSidebarFolderActionsArgs {
  section: Section;
  refresh: () => void;
  setFolders?: (folders: { id: string; name: string }[]) => void;
}

export interface AddFolderOptions {
  spaceId?: string;
  parentId?: string;
}

export interface SidebarFolderActions {
  handleAddFolder: (options?: AddFolderOptions) => void;
  handleFolderRename: (folder: Folder) => void;
  handleFolderDelete: (folder: Folder) => Promise<void>;
  handleCreateFolderOrUpdate: (
    name: string,
    isEditing?: boolean,
    id?: string,
  ) => Promise<void>;
  modalState: {
    open: boolean;
    spaceId: string | undefined;
    parentId: string | undefined;
    updateTarget: Folder | null;
    close: () => void;
  };
}

export const useSidebarFolderActions = ({
  section,
  refresh,
  setFolders,
}: UseSidebarFolderActionsArgs): SidebarFolderActions => {
  const setNotification = useNotificationStore(s => s.setNotification);

  const [open, setOpen] = useState(false);
  const [spaceId, setSpaceId] = useState<string | undefined>();
  const [parentId, setParentId] = useState<string | undefined>();
  const [updateTarget, setUpdateTarget] = useState<Folder | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setSpaceId(undefined);
    setParentId(undefined);
    setUpdateTarget(null);
  }, []);

  const handleAddFolder = useCallback(
    (options?: AddFolderOptions) => {
      if (options?.parentId) {
        const { nodes } = useSidebarTreeStore.getState();
        if (wouldExceedDepth(nodes, options.parentId, 1)) {
          setNotification({
            type: NotificationType.Error,
            message: `Folder depth limit reached (max ${MAX_FOLDER_DEPTH} levels).`,
          });
          return;
        }
      }
      setUpdateTarget(null);
      setSpaceId(options?.spaceId);
      setParentId(options?.parentId);
      setOpen(true);
    },
    [setNotification],
  );

  const handleFolderRename = useCallback((folder: Folder) => {
    setUpdateTarget(folder);
    setOpen(true);
  }, []);

  const handleFolderDelete = useCallback(
    async (folder: Folder) => {
      if (!window.confirm(`Delete folder "${folder.name}"?`)) return;
      try {
        await FolderService.deleteFolder(folder.id);
        setNotification({
          type: NotificationType.Success,
          message: 'Folder deleted successfully',
        });
        refresh();
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as Error),
        });
      }
    },
    [refresh, setNotification],
  );

  const handleCreateFolderOrUpdate = useCallback(
    async (name: string, isEditing?: boolean, id?: string) => {
      const trimmed = name?.trim();
      if (!trimmed) return;
      try {
        if (isEditing && id) {
          await FolderService.updateFolder(id, { name: trimmed });
          setNotification({
            type: NotificationType.Success,
            message: 'Folder renamed successfully',
          });
        } else if (spaceId) {
          await FolderService.createFolderOnSpace({
            name: trimmed,
            spaceId,
            section,
            ...(parentId ? { parentId } : {}),
          });
          setNotification({
            type: NotificationType.Success,
            message: 'Folder created successfully',
          });
        } else {
          const res = await FolderService.createFolder({
            name: trimmed,
            section,
            ...(parentId ? { parentId } : {}),
          });
          setNotification({
            type: NotificationType.Success,
            message: 'Folder created successfully',
          });
          if (setFolders) {
            const { data } = await FolderService.getFolders();
            setFolders(data ?? []);
          }
          void res;
        }
        close();
        refresh();
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error as Error),
        });
      }
    },
    [section, spaceId, parentId, refresh, setFolders, setNotification, close],
  );

  return {
    handleAddFolder,
    handleFolderRename,
    handleFolderDelete,
    handleCreateFolderOrUpdate,
    modalState: { open, spaceId, parentId, updateTarget, close },
  };
};
